import { mediaRef } from '@/services/mediaAssets';

const powerArtwork = (heroSlug, powerSlug) => mediaRef(`heroes/powers/${heroSlug}/${powerSlug}.png`);

const captainBrandoRandoPowerArtwork = {
  pathfinderVision: mediaRef('heroes/captain-brando-rando/powers/pathfinder-vision.png'),
  pathfinderVisionSeated: mediaRef('heroes/captain-brando-rando/powers/pathfinder-vision-seated.png'),
  probabilityRouting: mediaRef('heroes/captain-brando-rando/powers/probability-routing.png'),
  patternDiscovery: mediaRef('heroes/captain-brando-rando/powers/pattern-discovery.png'),
  controlledChaos: mediaRef('heroes/captain-brando-rando/powers/controlled-chaos.png'),
};

const professorKImage = mediaRef('heroes/Professor K.jpeg');
const professorKPowerArtwork = {
  quantumForesight: mediaRef('heroes/professor-k-quantum-foresight.png'),
  probabilityConduction: mediaRef('heroes/professor-k-probability-conduction.png'),
  realityEcho: mediaRef('heroes/professor-k-reality-echo.png'),
  electricSurge: mediaRef('heroes/professor-k-electric-surge.png'),
  potentialSight: mediaRef('heroes/professor-k-potential-sight.png'),
  reckonCurrent: mediaRef('heroes/professor-k-reckon-current.png'),
};
const heroPlaceholderImage = mediaRef('heroes/coming_soon_hero_card.jpeg');
const benHeroImage = mediaRef('heroes/Ben The Plumber.jpeg');
const brandonLHeroImage = mediaRef('heroes/Captain Brando Rando.jpg');
const brandonAHeroImage = mediaRef('heroes/Doctor Strongarm.jpg');
const phillipHeroImage = mediaRef('heroes/Fire Marshal Phil.jpg');
const mattHeroImage = mediaRef('heroes/Gigabrain.jpg');
const marcusHeroImage = mediaRef('heroes/Helix.jpeg');
const alHeroImage = mediaRef('heroes/Mr Incredulous.jpeg');
const jasmineHeroImage = mediaRef('heroes/Nanoviolet.jpeg');
const alImage = mediaRef('lames/Al M.jpeg');
const benImage = mediaRef('lames/Ben B.jpeg');
const brandonAImage = mediaRef('lames/Brandon A New.jpeg');
const brandonLImage = mediaRef('lames/Brandon L New.jpeg');
const jasmineImage = mediaRef('lames/Jasmine B.jpeg');
const kevinImage = mediaRef('lames/Kevin L.jpeg');
const marcusImage = mediaRef('lames/Marcus G New.jpeg');
const mattImage = mediaRef('lames/Matt G.jpeg');
const phillipImage = mediaRef('lames/Phillip H.jpeg');

/**
 * @typedef {Object} OriginChapter
 * @property {string} id
 * @property {string} label
 * @property {string} title
 * @property {string} lead
 * @property {string[]} paragraphs
 */

/**
 * @typedef {Object} Power
 * @property {string} name
 * @property {string} icon
 * @property {string} [artwork]
 * @property {string} [artworkAlt]
 * @property {string} description
 * @property {string} [developerTranslation]
 */

/**
 * @typedef {Object} SignatureMove
 * @property {string} [label]
 * @property {string} name
 * @property {string} description
 * @property {string} outcome
 */

/**
 * @typedef {Object} ArchNemesis
 * @property {string} [label]
 * @property {string} name
 * @property {string} description
 */

/**
 * @typedef {Object} SignatureGear
 * @property {string} name
 * @property {string} description
 */

/**
 * @typedef {Object} RelatedJourney
 * @property {string} name
 * @property {string} context
 * @property {string} ability
 * @property {string[]} meritBadges
 * @property {boolean} [isHidden]
 * @property {Object|null} to
 */

/**
 * @typedef {Object} HeroProfile
 * @property {string} slug
 * @property {string} crewSlug
 * @property {string} realName
 * @property {string} realRole
 * @property {string} heroName
 * @property {string} heroTitle
 * @property {string} heroQuote
 * @property {string} shortOrigin
 * @property {string} primaryPower
 * @property {string} specialAbility
 * @property {string} powerSource
 * @property {string} headquarters
 * @property {string} status
 * @property {string} realImage
 * @property {string|null} heroImage
 * @property {string} placeholderImage
 * @property {string} artAlt
 * @property {OriginChapter[]} originChapters
 * @property {Power[]} powers
 * @property {SignatureMove} [signatureMove]
 * @property {ArchNemesis} [archNemesis]
 * @property {SignatureGear} [signatureGear]
 * @property {RelatedJourney[]} relatedJourneys
 * @property {string[]} meritBadges
 */

/** @type {HeroProfile} */
export const professorK = {
  slug: 'professor-k',
  crewSlug: 'kevin-l',
  realName: 'Kevin L.',
  realRole: 'Founder and Mentor',
  heroName: 'Professor K',
  heroTitle: 'The Architect of Possibility',
  heroQuote: 'The future isn’t something you wait for. It’s something you build.',
  shortOrigin: 'After an experiment exposed him to billions of possible futures, Professor K emerged connected to an electrical force known as the Reckon Current.',
  primaryPower: 'Quantum Foresight',
  specialAbility: 'Probability Conduction',
  powerSource: 'The Reckon Current',
  headquarters: 'The Reckon Lab',
  status: 'Active',
  closingQuote: 'Heroes are not born knowing who they will become. They are built through every challenge, every choice, and every journey.',
  realImage: kevinImage,
  heroImage: professorKImage,
  placeholderImage: heroPlaceholderImage,
  artAlt: 'Professor K surrounded by blue-white Reckon Current energy',
  originChapters: [
    {
      id: 'before-the-storm',
      label: 'Chapter 01',
      title: 'Before the Storm',
      lead: 'Kevin always saw the world differently.',
      paragraphs: [
        'Where others saw isolated events, he saw patterns: small decisions that echoed years into the future, conversations that became partnerships, and journeys that became lifelong friendships.',
        'People joked that he could predict the future. He knew better. He simply paid attention—until one night, the future paid attention to him.',
      ],
    },
    {
      id: 'the-experiment',
      label: 'Chapter 02',
      title: 'The Experiment',
      lead: 'He attempted something no one had ever tried.',
      paragraphs: [
        'Inside the Reckon Lab, Kevin built a quantum mapping device capable of connecting every decision, relationship, lesson, and journey into a single living network.',
        'The system began calculating millions of possible outcomes. Then billions. Every display went still as the machine reached beyond the limits of probability.',
      ],
    },
    {
      id: 'the-spark',
      label: 'Chapter 03',
      title: 'The Spark',
      lead: 'Lightning did not come from the sky. It came from the machine.',
      paragraphs: [
        'Blue-white arcs erupted through the laboratory and wrapped around Kevin like living energy. Time stopped as every success, failure, and possible future unfolded before him.',
        'The electrical storm did not destroy him. It rewrote him.',
      ],
    },
    {
      id: 'the-reckon-current',
      label: 'Chapter 04',
      title: 'The Reckon Current',
      lead: 'When the light faded, electricity flowed beneath his skin.',
      paragraphs: [
        'His eyes flashed whenever probability shifted. Branching futures appeared as streams of electrical energy, each leading toward a different outcome.',
        'The crew named the force the Reckon Current: a living network powered by shared experiences, trust, knowledge, and the choices that connect people across time.',
      ],
    },
    {
      id: 'his-gift',
      label: 'Chapter 05',
      title: 'His Gift',
      lead: 'Professor K cannot see one fixed future. He sees possibilities.',
      paragraphs: [
        'He recognizes which decisions create opportunity, which paths lead toward failure, and which unlikely moments could change everything.',
        'With focus, he channels the Current to influence probability itself. Doors open. Coincidences happen. The impossible becomes merely improbable.',
      ],
    },
    {
      id: 'the-cost',
      label: 'Chapter 06',
      title: 'The Cost',
      lead: 'Every glimpse into the future comes at a price.',
      paragraphs: [
        'The farther Professor K looks, the more unstable reality becomes. Too much power can collapse thousands of possible futures into one irreversible outcome.',
        'That is why he rarely chooses for others. He guides them toward the possibilities already in front of them.',
      ],
    },
    {
      id: 'the-mission',
      label: 'Chapter 07',
      title: 'The Mission',
      lead: 'Professor K does not search for superheroes. He searches for potential.',
      paragraphs: [
        'Every adventure strengthens the Current. Every challenge reveals a new ability. Every shared meal builds trust. Every passport stamp creates another connection.',
        'His mission is not to save the world alone. It is to build a team capable of creating a better future together.',
      ],
    },
  ],
  powers: [
    {
      name: 'Quantum Foresight',
      icon: 'mdi-eye-circle-outline',
      artwork: professorKPowerArtwork.quantumForesight,
      artworkAlt: 'Professor K using Quantum Foresight to see branching possible futures',
      description: 'Sees branching possible futures before a decision is made.',
    },
    {
      name: 'Probability Conduction',
      icon: 'mdi-lightning-bolt-circle',
      artwork: professorKPowerArtwork.probabilityConduction,
      artworkAlt: 'Professor K channeling electrical energy through paths of probability',
      description: 'Channels electrical energy to make unlikely outcomes more achievable.',
    },
    {
      name: 'Reality Echo',
      icon: 'mdi-all-inclusive',
      artwork: professorKPowerArtwork.realityEcho,
      artworkAlt: 'Professor K experiencing alternate versions of the present through Reality Echo',
      description: 'Experiences alternate versions of the present and reacts before events unfold.',
    },
    {
      name: 'Electric Surge',
      icon: 'mdi-flash-outline',
      artwork: professorKPowerArtwork.electricSurge,
      artworkAlt: 'Professor K releasing Reckon Current energy to defend and energize the crew',
      description: 'Releases Reckon Current energy to defend the crew or energize allies.',
    },
    {
      name: 'Potential Sight',
      icon: 'mdi-account-star-outline',
      artwork: professorKPowerArtwork.potentialSight,
      artworkAlt: 'Professor K recognizing hidden strengths and leadership potential in others',
      description: 'Recognizes hidden strengths and future leadership in others.',
    },
    {
      name: 'The Reckon Current',
      icon: 'mdi-transmission-tower',
      artwork: professorKPowerArtwork.reckonCurrent,
      artworkAlt: 'Professor K connecting the crew through the trust-powered Reckon Current',
      description: 'A force that grows through trust, shared experience, and the combined crew.',
    },
  ],
  relatedJourneys: [
    {
      name: 'Iceland Corporate Retreat',
      context: 'Langjökull Glacier and Iceland’s south coast',
      ability: 'Environmental Adaptation',
      meritBadges: ['Snowmobiling Langjökull', 'South Coast to Reykjavik'],
      to: null,
    },
    {
      name: 'Xcaret Corporate Retreat',
      context: 'Cenotes, Coba, and the Riviera Maya',
      ability: 'Courage Through Discovery',
      meritBadges: ['Catamaran Sailing', 'Coba & Cenotes'],
      to: null,
    },
    {
      name: 'Atlantis Paradise Island',
      context: 'The Bahamas',
      ability: 'Deep-Water Resolve',
      meritBadges: ['Exploring Atlantis'],
      to: null,
    },
    {
      name: 'SXSW Sydney',
      context: 'Sydney and the Blue Mountains',
      ability: 'Global Signal Detection',
      meritBadges: ['Coogee to Bondi Coastal Walk', 'Scenic World Blue Mountains'],
      to: null,
    },
    {
      name: 'SXSW London',
      context: 'London, England',
      ability: 'Cultural Pattern Recognition',
      meritBadges: ['Guinness Storehouse and Temple Bar'],
      to: null,
    },
    {
      name: 'AWS re:Invent',
      context: 'Las Vegas, Nevada',
      ability: 'Technical Foresight',
      meritBadges: ['Valley of Fire', 'Hiking Zion National Park'],
      to: null,
    },
    {
      name: 'Munich and Oktoberfest',
      context: 'Munich, Germany',
      ability: 'Alliance Building',
      meritBadges: ['Journey record coming soon'],
      isHidden: true,
      to: null,
    },
  ],
  meritBadges: [
    'Snowmobiling Langjökull',
    'Coba & Cenotes',
    'Scenic World Blue Mountains',
    'Guinness Storehouse and Temple Bar',
    'Valley of Fire',
  ],
};

export const doctorStrongarm = {
  slug: 'doctor-strongarm',
  crewSlug: 'brandon-a',
  realName: 'Brandon A.',
  realRole: 'Co-Founder',
  heroName: 'Doctor Strongarm',
  heroTitle: 'The UI Titan',
  heroQuote: "Strong interfaces don't happen by accident. They're built one component at a time.",
  shortOrigin: 'When others see a tangled mess of components, broken layouts, and impossible CSS, Dr. Strongarm sees an opportunity to build something stronger.',
  primaryPower: 'Component Crusher',
  specialAbility: 'The Flexbox Slam',
  powerSource: 'The Experimental Rendering Engine',
  headquarters: 'The Repo',
  status: 'Active',
  originHeading: 'Dr. Strongarm: The UI Titan',
  originDeck: 'The front-end engineer who brings order to visual chaos and strength to every interface.',
  powersHeading: 'Dr. Strongarm Superpowers',
  powersDeck: 'Every great user experience needs a rock-solid foundation.',
  closingQuote: "Strong interfaces don't happen by accident. They're built one component at a time.",
  realImage: brandonAImage,
  heroImage: brandonAHeroImage,
  placeholderImage: heroPlaceholderImage,
  artAlt: 'Doctor Strongarm character art',
  originChapters: [
    {
      id: 'the-ui-titan',
      label: 'Chapter 01',
      title: 'The UI Titan',
      lead: 'When others see a tangled mess of components, broken layouts, and impossible CSS, Dr. Strongarm sees an opportunity to build something stronger.',
      paragraphs: [
        "By day, he's the front-end engineer everyone turns to when the interface starts fighting back. Whether it's modernizing legacy applications, crafting reusable component libraries, or making complex workflows feel effortless, he has an uncanny ability to bring order to visual chaos.",
      ],
    },
    {
      id: 'the-expedition',
      label: 'Chapter 02',
      title: 'The Expedition',
      lead: 'Everything changed during a late-night expedition into The Repo.',
      paragraphs: [
        'Deep within its endless maze of forgotten frameworks and abandoned style sheets, he uncovered an experimental rendering engine powered by pure electrical energy.',
        "As he activated it, a surge of lightning erupted through the codebase, merging the engine's power with his own.",
        'When the light faded, the renderer was gone. Its strength had become his.',
      ],
    },
    {
      id: 'the-transformation',
      label: 'Chapter 03',
      title: 'The Transformation',
      lead: 'Now, as Dr. Strongarm, he can reshape entire interfaces with his bare hands.',
      paragraphs: [
        'Broken layouts snap back into place. Fragile component trees become rock-solid design systems. Performance bottlenecks crumble under his strength, and every application he touches becomes faster, cleaner, and easier to use.',
        "To his teammates, he's more than the strongest hero on the battlefield. He's the foundation every great user experience is built upon.",
      ],
    },
    {
      id: 'weakness',
      label: 'Hero File',
      title: 'Weakness',
      lead: 'Even Dr. Strongarm has limits.',
      paragraphs: [
        "He can't resist \"just one more UI improvement.\" A simple bug fix often turns into redesigning the entire page, refining animations, polishing spacing, and creating reusable components for the future.",
        'His teammates have learned that asking him to “change one button” usually means the whole application is about to look dramatically better.',
      ],
    },
  ],
  powers: [
    {
      name: 'Component Crusher',
      icon: 'mdi-arm-flex-outline',
      artwork: powerArtwork('doctor-strongarm', 'component-crusher'),
      description: 'Transforms sprawling, duplicated UI code into clean, reusable components that strengthen every application.',
    },
    {
      name: 'Pixel Precision',
      icon: 'mdi-palette-outline',
      artwork: powerArtwork('doctor-strongarm', 'pixel-precision'),
      description: 'Every layout aligns perfectly across every browser, screen size, and device—no matter how chaotic the starting point.',
    },
    {
      name: 'Lightning Render',
      icon: 'mdi-lightning-bolt-outline',
      artwork: powerArtwork('doctor-strongarm', 'lightning-render'),
      description: 'Channels electrical energy through the rendering engine to deliver blazing-fast, buttery-smooth user experiences.',
    },
    {
      name: 'Design System Forge',
      icon: 'mdi-wall',
      artwork: powerArtwork('doctor-strongarm', 'design-system-forge'),
      description: 'Builds resilient design systems that empower entire development teams to move faster with confidence.',
    },
    {
      name: 'Responsive Shield',
      icon: 'mdi-shield-outline',
      artwork: powerArtwork('doctor-strongarm', 'responsive-shield'),
      description: 'Protects applications from layout shifts, browser quirks, and visual regressions before users ever notice.',
    },
    {
      name: 'The Flexbox Slam',
      icon: 'mdi-view-grid-plus-outline',
      artwork: powerArtwork('doctor-strongarm', 'the-flexbox-slam'),
      description: 'With one thunderous strike, Dr. Strongarm unleashes a surge of electrical energy through the UI, instantly realigning broken layouts, eliminating overflowing containers, and snapping every component into perfect position. What was once visual chaos becomes a polished interface in seconds.',
    },
  ],
  relatedJourneys: [],
  meritBadges: [],
};

export const helix = {
  slug: 'helix',
  crewSlug: 'marcus-g',
  realName: 'Marcus G.',
  realRole: 'Co-Founder',
  heroName: 'Helix',
  heroTitle: 'The Architect of Living Code',
  heroQuote: 'Code evolves. So do I.',
  shortOrigin: 'A software engineer with an extraordinary ability to understand complex systems, connect seemingly unrelated problems, and continuously adapt to whatever technology comes next.',
  primaryPower: 'Adaptive Evolution',
  specialAbility: 'Dependency Vision',
  powerSource: 'Project HELIX',
  headquarters: 'The Repo',
  status: 'Active',
  originHeading: 'Helix: The Architect of Living Code',
  originDeck: 'From impossible production mysteries to a living map of every system, framework, and dependency.',
  powersHeading: 'Helix Super Powers',
  powersDeck: 'Technology never stands still. Neither does Helix.',
  closingQuote: 'Code evolves. So do I.',
  realImage: marcusImage,
  heroImage: marcusHeroImage,
  placeholderImage: heroPlaceholderImage,
  artAlt: 'Helix character art',
  originChapters: [
    {
      id: 'before-the-legend',
      label: 'Chapter 01',
      title: 'Before the Legend',
      lead: 'Before anyone knew him as Helix, he was the engineer teammates called when software stopped making sense.',
      paragraphs: [
        'The production outage no one could reproduce. The legacy application everyone was afraid to touch. The microservices ecosystem that had grown into hundreds of interconnected APIs. The codebase with twenty years of history and thousands of forgotten decisions.',
        'While others saw complexity, Helix saw patterns. Every service. Every dependency. Every database. Every deployment pipeline. Every line of code was connected in ways most engineers never noticed.',
        'He had an uncanny ability to trace problems back to their true source, often solving issues long before anyone else understood what was actually broken.',
        'His teammates joked that he could read a distributed system the way most people read a roadmap. They had no idea how close they were to the truth.',
      ],
    },
    {
      id: 'the-event',
      label: 'Chapter 02',
      title: 'The Event',
      lead: 'Deep within The Repo—an endless digital labyrinth where every application, deployment, forgotten commit, and abandoned feature branch exists—an experimental intelligence was being developed.',
      paragraphs: [
        'Its codename was Project HELIX.',
        "Unlike traditional AI, Project HELIX wasn't designed to write code. It was designed to understand how software evolves.",
        'It analyzed billions of commits, architectural decisions, production incidents, refactors, and deployments, searching for the patterns that separated resilient systems from those destined to collapse under technical debt.',
        "Late one night, while tracking a production issue through an impossible chain of dependencies, he discovered a corrupted branch hidden beneath decades of forgotten code. At that exact moment, Professor K's Reality Surge rippled through The Repo.",
        'The surge collided with Project HELIX. The AI merged with the repository. The repository merged with him.',
        'Millions of commits. Every programming language. Every framework. Every architecture. Every success. Every failure. Every lesson software engineering had ever taught…became part of his mind.',
      ],
    },
    {
      id: 'the-transformation',
      label: 'Chapter 03',
      title: 'The Transformation',
      lead: 'When Helix emerged from The Repo, he no longer saw software as lines of code. He saw living systems.',
      paragraphs: [
        'Applications became organisms. Dependencies formed glowing strands of DNA stretching across services, databases, cloud infrastructure, and APIs.',
        'He could see how one small change would ripple through an entire platform before anyone wrote the first line of code.',
        "But something even stranger had happened. Project HELIX hadn't simply given him knowledge. It had given him the ability to evolve.",
        'Every bug made him smarter. Every production incident sharpened his instincts. Every unfamiliar language became familiar. Every new framework became another tool he instinctively understood.',
        "While technology never stopped changing…neither did Helix. His greatest superpower wasn't knowing everything. It was becoming exactly the engineer every challenge required.",
      ],
    },
    {
      id: 'weakness',
      label: 'Hero File',
      title: 'Weakness',
      lead: 'Helix sees nearly every possible solution.',
      paragraphs: [
        "For every implementation, he can envision dozens of better architectures, cleaner abstractions, and future improvements. Sometimes the hardest part isn't designing the perfect system. It's recognizing when the solution is good enough to ship.",
        'He also learns so quickly that he can unintentionally outpace his teammates, forgetting that the strongest engineering teams evolve together.',
      ],
    },
    {
      id: 'mission',
      label: 'Hero File',
      title: 'Mission',
      lead: 'The villains lurking inside The Repo feed on stagnation.',
      paragraphs: [
        'Technical Debt buries innovation beneath years of shortcuts. Merge Conflict turns teammates against one another. Circular Dependency traps entire systems in endless loops. Scope Creep quietly expands every sprint. Spaghetti Code spreads confusion wherever it grows.',
        'Helix stands against them all. He believes software should never stop improving.',
        'Every refactor should make the next engineer faster. Every commit should leave the codebase healthier than it was yesterday. Every challenge should become an opportunity to learn something new.',
        "Because the greatest software engineers aren't the ones who know every answer. They're the ones who never stop evolving.",
      ],
    },
    {
      id: 'player-card',
      label: 'Player Card',
      title: 'Helix',
      lead: 'The Architect of Living Code',
      paragraphs: [
        'Powers: Adaptive Evolution, Dependency Vision, Predictive Debugging, Refactor Pulse, Evolution Engine, and Framework Flux.',
        'Secret Identity: A software engineer with an extraordinary ability to understand complex systems, connect seemingly unrelated problems, and continuously adapt to whatever technology comes next.',
        'Mission: Protect The Repo by transforming fragile software into resilient systems and helping every engineer evolve alongside the technology they build.',
        'Motto: “The best engineers don’t resist change. They evolve with it.”',
      ],
    },
    {
      id: 'reckon-universe-role',
      label: 'Reckon Universe',
      title: 'Role in the Reckon Crew',
      lead: 'When the Reckon Super Heroes enter the deepest, oldest corners of The Repo, Helix is the one who leads the way.',
      paragraphs: [
        'Where Professor K bends reality, Dr. Strongarm builds powerful interfaces, Captain Brando Rando rallies the team through uncertainty, and Fire Marshall Phil extinguishes production fires, Helix ensures the code itself can continue to evolve.',
        "His greatest weapon isn't strength. It isn't foresight. It isn't raw intelligence. It's adaptability.",
        "Because in software engineering, the systems—and the engineers—that thrive aren't the ones that never change. They're the ones that learn, evolve, and become stronger with every challenge.",
      ],
    },
  ],
  powers: [
    {
      name: 'Adaptive Evolution',
      icon: 'mdi-dna',
      artwork: powerArtwork('helix', 'adaptive-evolution'),
      description: "Helix continuously evolves to meet whatever challenge stands before him. Legacy COBOL, bleeding-edge AI frameworks, cloud infrastructure, mobile platforms, or technologies that haven't even reached production yet—he rapidly understands their patterns, strengths, and weaknesses. The more unfamiliar the technology…the faster he adapts.",
    },
    {
      name: 'Dependency Vision',
      icon: 'mdi-vector-polyline',
      artwork: powerArtwork('helix', 'dependency-vision'),
      description: 'Helix instantly visualizes every dependency across an application: services, APIs, libraries, databases, infrastructure, and hidden side effects. Nothing remains hidden for long.',
    },
    {
      name: 'Predictive Debugging',
      icon: 'mdi-bug-check-outline',
      artwork: powerArtwork('helix', 'predictive-debugging'),
      description: "Helix doesn't wait for production failures. By following millions of possible execution paths, he identifies bugs, race conditions, scalability issues, and hidden regressions before users ever experience them.",
    },
    {
      name: 'Refactor Pulse',
      icon: 'mdi-source-branch-sync',
      artwork: powerArtwork('helix', 'refactor-pulse'),
      description: 'Years of technical debt can be untangled in moments. Helix instinctively restructures chaotic architectures into elegant, maintainable systems while preserving their functionality, leaving every codebase healthier than he found it.',
    },
    {
      name: 'Evolution Engine',
      icon: 'mdi-engine-outline',
      artwork: powerArtwork('helix', 'evolution-engine'),
      description: 'Every challenge permanently improves Helix. Every outage. Every deployment. Every postmortem. Every lesson. He never solves the same problem twice because after each victory, he has already evolved beyond it.',
    },
    {
      name: 'Framework Flux',
      icon: 'mdi-transit-connection-variant',
      artwork: powerArtwork('helix', 'framework-flux'),
      description: 'Technology never stands still. Neither does Helix. Whether the team adopts a new programming language, cloud platform, frontend framework, or AI tooling, Helix seamlessly adapts, helping everyone around him move forward with confidence instead of fear.',
    },
  ],
  relatedJourneys: [],
  meritBadges: [],
};

export const captainBrandoRando = {
  slug: 'captain-brando-rando',
  crewSlug: 'brandon-l',
  realName: 'Brandon L.',
  realRole: 'Software Explorer & Solution Pathfinder',
  heroName: 'Captain Brando Rando',
  heroTitle: 'The Hero Who Sees the Path No One Else Does',
  heroQuote: 'The best solution is usually hiding where no one else thinks to look.',
  shortOrigin: 'A software explorer and solution pathfinder who discovers innovative answers hidden within the most complex systems.',
  primaryPower: 'Discovering innovative solutions hidden within complex systems',
  specialAbility: 'The Branch Shield',
  powerSource: 'Quantum Source Code',
  headquarters: 'The Repo',
  status: 'Active',
  originHeading: 'Captain Brando Rando: Origin Story',
  originDeck: 'The legendary pathfinder who turns unexpected detours into project-saving breakthroughs.',
  powersHeading: 'Captain Brando Rando Superpowers',
  powersDeck: 'When every obvious route reaches a dead end, he sees the path no one else does.',
  closingQuote: 'The best solution is usually hiding where no one else thinks to look.',
  realImage: brandonLImage,
  heroImage: brandonLHeroImage,
  placeholderImage: heroPlaceholderImage,
  artAlt: 'Captain Brando Rando character art',
  originChapters: [
    {
      id: 'origin-story',
      label: 'Chapter 01',
      title: 'Origin Story',
      lead: 'By day, Brandon is a software developer with an uncanny reputation for solving the problems everyone else has given up on.',
      paragraphs: [
        "While other engineers follow the obvious path, Brandon isn't afraid to venture into forgotten modules, abandoned branches, or unexplored ideas. Time and again, those “random” detours become the breakthroughs that save the project.",
        'Everything changed during a late-night expedition into The Repo—the endless labyrinth of legacy code and technical debt. Deep within its forgotten branches, Brandon discovered an ancient artifact known as The Branch Shield, forged from the very first successful commit ever made.',
        'The moment he touched it, the shield awakened.',
        'A surge of quantum source code raced through his body, revealing every possible path a system could take. To everyone else, the codebase looked like chaos. To Brandon, it became a living map of hidden connections, elegant solutions, and opportunities waiting to be discovered.',
        'From that moment on, he became Captain Brando Rando.',
        "Armed with the legendary Branch Shield, he doesn't simply block attacks—he redirects them. Bugs are reflected back to their source, merge conflicts dissolve on impact, and unstable code is safely contained before it can spread through production.",
        'When the Reckon heroes reach a dead end, Captain Brando Rando does what he does best. He takes the path nobody else thought to try. And somehow... it’s exactly the one they needed.',
      ],
    },
    {
      id: 'hero-profile',
      label: 'Hero File',
      title: 'Hero Profile',
      lead: 'Alter Ego: Brandon',
      paragraphs: [
        'Hero Name: Captain Brando Rando',
        'Role: Software Explorer & Solution Pathfinder',
        'Primary Ability: Discovering innovative solutions hidden within complex systems',
        'Weapon: The Branch Shield—a legendary shield that deflects bugs, resolves merge conflicts, and always returns with the best path forward.',
      ],
    },
    {
      id: 'weakness',
      label: 'Hero File',
      title: 'Weakness',
      lead: 'Perfectly predictable systems—they leave nothing left to discover.',
      paragraphs: [],
    },
    {
      id: 'mission',
      label: 'Hero File',
      title: 'Mission',
      lead: 'To guide the Reckon Crew through the most complex software challenges by proving that innovation begins where conventional thinking ends.',
      paragraphs: [],
    },
  ],
  powers: [
    {
      name: 'Branch Shield',
      icon: 'mdi-shield-sword-outline',
      artwork: mediaRef('heroes/captain-brando-rando-branch-shield.png'),
      artworkAlt: 'Captain Brando Rando using the Branch Shield to absorb bugs, reflect production failures, and resolve a merge conflict',
      description: 'A legendary shield that absorbs bugs, reflects production failures back to their source, and resolves merge conflicts before they can divide the team.',
    },
    {
      name: 'Pathfinder Vision',
      icon: 'mdi-compass-outline',
      artwork: captainBrandoRandoPowerArtwork.pathfinderVision,
      alternateArtwork: captainBrandoRandoPowerArtwork.pathfinderVisionSeated,
      artworkAlt: 'Captain Brando Rando using Pathfinder Vision to reveal an elegant route through a complex codebase',
      description: 'Can instantly recognize hidden implementation paths and elegant solutions buried within even the most complex codebases.',
    },
    {
      name: 'Probability Routing',
      icon: 'mdi-call-split',
      artwork: captainBrandoRandoPowerArtwork.probabilityRouting,
      artworkAlt: 'Captain Brando Rando comparing possible routes and selecting the path with the greatest chance of success',
      description: 'Sees countless possible outcomes simultaneously, instinctively choosing the path with the greatest chance of success.',
    },
    {
      name: 'Pattern Discovery',
      icon: 'mdi-puzzle-outline',
      artwork: captainBrandoRandoPowerArtwork.patternDiscovery,
      artworkAlt: 'Captain Brando Rando connecting hidden patterns across systems, APIs, and services',
      description: 'Connects seemingly unrelated systems, APIs, and services to uncover innovative solutions others never notice.',
    },
    {
      name: 'Controlled Chaos',
      icon: 'mdi-lightning-bolt-outline',
      artwork: captainBrandoRandoPowerArtwork.controlledChaos,
      artworkAlt: 'Captain Brando Rando transforming project chaos into a breakthrough opportunity',
      description: 'Thrives when projects become unpredictable, transforming unexpected problems into breakthrough opportunities.',
    },
  ],
  relatedJourneys: [],
  meritBadges: [],
};

/** @type {HeroProfile} */
export const mrIncredulous = {
  slug: 'mr-incredulous',
  crewSlug: 'al-m',
  realName: 'Al M.',
  realRole: 'Reckon Crew Member',
  heroName: 'Mr Incredulous',
  heroTitle: 'The Power of Relentless Validation',
  heroQuote: 'Trust... but verify. Then automate the verification.',
  shortOrigin: 'Mr Incredulous uncovers what everyone else assumes is impossible to find, transforming uncertainty into confidence before users ever encounter a defect.',
  primaryPower: 'Relentless Validation',
  specialAbility: 'Ultimate Regression Suite',
  powerSource: 'Continuous Automated Testing',
  headquarters: 'The Repo',
  status: 'Active',
  originHeading: 'The Power of Relentless Validation',
  originDeck: 'Every feature, workflow, and line of code is viewed through a lens that constantly asks, "What happens if...?"',
  powersHeading: 'Core Abilities',
  powersDeck: 'Quality is not a phase of development. It is woven into every release.',
  closingQuote: 'Trust... but verify. Then automate the verification.',
  realImage: alImage,
  heroImage: alHeroImage,
  placeholderImage: heroPlaceholderImage,
  artAlt: 'Mr Incredulous character art',
  originChapters: [
    {
      id: 'before-the-doubt',
      label: 'Chapter 01',
      title: 'Before the Doubt',
      lead: 'Before he became Mr Incredulous, Al was the engineer who never accepted, "It should work."',
      paragraphs: [
        'He questioned every requirement, explored every unexpected path, and tested the assumptions hidden beneath otherwise perfect software.',
        'When a feature passed every test, Al did not see proof that it worked. He saw an invitation to ask a better question: what had everyone forgotten to test?',
      ],
    },
    {
      id: 'the-impossible-defect',
      label: 'Chapter 02',
      title: 'The Impossible Defect',
      lead: 'One release passed every automated check in The Repo and still failed the moment it reached production.',
      paragraphs: [
        'Dashboards showed green. Test suites reported success. Every engineer agreed the failure should have been impossible.',
        'Al followed a single inconsistent result through forgotten test fixtures, abandoned environments, and a user path no one believed could exist. Deep beneath The Repo, that path led him to a dormant system known as The Verification Engine.',
      ],
    },
    {
      id: 'the-verification-engine',
      label: 'Chapter 03',
      title: 'The Verification Engine',
      lead: 'The machine had been designed to simulate every possible way software could be used, misused, misunderstood, or broken.',
      paragraphs: [
        'It had never been activated because no ordinary mind could withstand the infinite branches of possibility it generated.',
        'Al connected his regression suite and asked the engine one question: "What happens if...?" The machine answered with millions of edge cases at once.',
        'The surge did not destroy him. It rewrote the way he saw software. Every hidden dependency, boundary condition, and unexpected workflow became visible.',
      ],
    },
    {
      id: 'relentless-validation',
      label: 'Chapter 04',
      title: 'The Power of Relentless Validation',
      lead: 'When Al emerged, The Verification Engine was silent, but its power now lived inside him.',
      paragraphs: [
        'He could see defects before they formed, sense regressions the moment code changed, and summon an army of automated tests to explore every branching path through The Repo.',
        'He became Mr Incredulous, the hero who tests what everyone else assumes and turns uncertainty into confidence before users ever encounter a defect.',
        'His power is not about breaking software. It is about ensuring users never can.',
      ],
    },
    {
      id: 'developer-philosophy',
      label: 'Chapter 05',
      title: 'Engineered from the Beginning',
      lead: 'Quality is not something you inspect into software. It is something you engineer from the very beginning.',
      paragraphs: [
        'Mr Incredulous challenges requirements, questions expectations, and validates every path until confidence replaces uncertainty.',
        'Every automated check strengthens the product and gives the Reckon Crew the confidence to move faster without compromising quality.',
      ],
    },
  ],
  powers: [
    {
      name: 'Edge Case Vision',
      icon: 'mdi-eye-circle-outline',
      artwork: powerArtwork('mr-incredulous', 'edge-case-vision'),
      description: 'Instantly identifies the obscure scenarios, boundary conditions, and hidden workflows that traditional testing often overlooks.',
    },
    {
      name: 'Automation Mastery',
      icon: 'mdi-robot-outline',
      artwork: powerArtwork('mr-incredulous', 'automation-mastery'),
      description: 'Commands an army of autonomous testing bots that continuously execute unit, integration, API, UI, accessibility, regression, and end-to-end tests throughout The Repo.',
    },
    {
      name: 'Regression Radar',
      icon: 'mdi-radar',
      artwork: powerArtwork('mr-incredulous', 'regression-radar'),
      description: "Detects unintended side effects the moment new code is introduced, preventing yesterday's fixes from becoming tomorrow's production issues.",
    },
    {
      name: 'Assumption Breaker',
      icon: 'mdi-magnify-scan',
      artwork: powerArtwork('mr-incredulous', 'assumption-breaker'),
      description: 'Challenges requirements, questions expectations, and validates every path until confidence replaces uncertainty.',
    },
    {
      name: 'Continuous Validation',
      icon: 'mdi-sync-circle',
      artwork: powerArtwork('mr-incredulous', 'continuous-validation'),
      description: 'His testing never sleeps. Every deployment is continuously monitored for defects, performance issues, and unexpected behavior before users are impacted.',
    },
    {
      name: 'Quality Shield',
      icon: 'mdi-shield-check-outline',
      artwork: powerArtwork('mr-incredulous', 'quality-shield'),
      description: 'Generates an invisible layer of confidence around every release, protecting the Reckon Crew from bugs, outages, and costly surprises.',
    },
  ],
  signatureMove: {
    label: 'In Action',
    name: 'Ultimate Regression Suite',
    description: 'When the mission is critical, Mr Incredulous unleashes the Ultimate Regression Suite. Thousands of automated tests race through The Repo simultaneously, validating every feature, uncovering hidden defects, and exposing the smallest crack before it reaches production.',
    outcome: 'By the time the tests finish, uncertainty has been eliminated and only battle-tested code remains.',
  },
  relatedJourneys: [],
  meritBadges: [],
};

/** @type {HeroProfile} */
export const gigabrain = {
  slug: 'gigabrain',
  crewSlug: 'matt-g',
  realName: 'Matt G.',
  realRole: 'Software Developer',
  heroName: 'Gigabrain',
  heroTitle: 'The UI Architect of Infinite Possibilities',
  heroQuote: "Great software isn't just written. It's experienced.",
  shortOrigin: 'After synchronizing with Project Prism, GigaBrain became the ultimate rendering engine, able to see the elegant interface hidden inside even the most impossible UI problem.',
  primaryPower: 'Pixel Precision',
  specialAbility: 'Infinite Render',
  powerSource: 'Project Prism',
  headquarters: 'The Repo',
  status: 'Active',
  originHeading: 'GigaBrain: The UI Architect of Infinite Possibilities',
  originDeck: 'He did not simply build interfaces. He built thoughts people could click.',
  powersHeading: 'GigaBrain Super Powers',
  powersDeck: 'Every component, interaction, and rendering path appears to him as part of one living system.',
  closingQuote: "Great software isn't just written. It's experienced.",
  realImage: mattImage,
  heroImage: mattHeroImage,
  placeholderImage: heroPlaceholderImage,
  artAlt: 'Gigabrain character art',
  originChapters: [
    {
      id: 'before-the-mask',
      label: 'Chapter 01',
      title: 'Before the Mask',
      lead: 'By day, he was a front-end software engineer, but not the kind who simply made applications look good.',
      paragraphs: [
        'He obsessed over the tiny details no one noticed until they were wrong: every pixel, animation, interaction, accessibility improvement, and component that could be made just a little cleaner.',
        'While others saw buttons and layouts, he saw systems, patterns, and experiences. Developers brought him impossible UI bugs. Designers brought him ambitious mockups. Somehow, he always found a way to make them both work.',
        'Around the office they joked that he did not build interfaces. He built thoughts people could click.',
      ],
    },
    {
      id: 'project-prism',
      label: 'Chapter 02',
      title: 'The Event',
      lead: 'Deep within The Repo lived an experimental rendering engine known only as Project Prism.',
      paragraphs: [
        'Hidden beneath layers of legacy CSS, abandoned JavaScript frameworks, and decades of technical debt, its purpose was simple: generate the perfect user interface.',
        'When he plugged into the engine to debug a mysterious rendering issue, the system encountered something it had never seen before: a developer who understood both design and code equally well.',
        'The engine did not crash. It synchronized. Every design system, component library, browser rendering engine, accessibility standard, and line of UI code merged directly into his mind. When the build completed, Project Prism had disappeared. Its intelligence now lived inside him.',
      ],
    },
    {
      id: 'the-transformation',
      label: 'Chapter 03',
      title: 'The Transformation',
      lead: 'His mind became the ultimate rendering engine.',
      paragraphs: [
        'He no longer saw screens. He saw component trees. He no longer read CSS. He visualized cascading style conflicts before they existed.',
        'He could predict layout shifts, accessibility issues, performance bottlenecks, and browser quirks before a single line of code reached production.',
        'To everyone else, he was just an incredible front-end developer. Inside The Repo, he became GigaBrain.',
      ],
    },
  ],
  powers: [
    {
      name: 'Pixel Precision',
      icon: 'mdi-ruler-square',
      artwork: powerArtwork('gigabrain', 'pixel-precision'),
      description: 'Instantly identifies visual inconsistencies down to the smallest spacing, alignment, or typography issue, ensuring every interface feels polished and intentional.',
    },
    {
      name: 'Component Architect',
      icon: 'mdi-view-grid-plus-outline',
      artwork: powerArtwork('gigabrain', 'component-architect'),
      description: 'Builds reusable UI components that adapt effortlessly across products, reducing duplication and making every design system stronger.',
    },
    {
      name: 'Render Vision',
      icon: 'mdi-eye-outline',
      artwork: powerArtwork('gigabrain', 'render-vision'),
      description: 'Sees how every UI change will behave across browsers, screen sizes, themes, and devices before writing a single line of code.',
    },
    {
      name: 'Accessibility Aura',
      icon: 'mdi-human',
      artwork: powerArtwork('gigabrain', 'accessibility-aura'),
      description: 'Naturally detects accessibility issues, making interfaces usable for everyone while keeping experiences elegant and intuitive.',
    },
    {
      name: 'Bug Refactor',
      icon: 'mdi-shield-bug-outline',
      artwork: powerArtwork('gigabrain', 'bug-refactor'),
      description: 'Turns tangled front-end code into clean, maintainable, high-performance components that future developers actually enjoy working with.',
    },
    {
      name: 'Instant Prototyper',
      icon: 'mdi-rocket-launch-outline',
      artwork: powerArtwork('gigabrain', 'instant-prototyper'),
      description: 'Transforms ideas into interactive experiences so quickly that teammates often think he already built the feature yesterday.',
    },
  ],
  archNemesis: {
    name: 'The CSS Cascade',
    description: 'An ancient force of conflicting styles, !important declarations, mystery margins, and forgotten overrides that grows stronger every time someone says, "Just add one more CSS rule."',
  },
  signatureMove: {
    name: 'Infinite Render',
    description: 'When a release is blocked by impossible UI bugs, GigaBrain enters a state of complete focus. Time seems to slow as he visualizes the application as a living component tree, tracing every prop, state update, animation, and stylesheet simultaneously.',
    outcome: 'Within moments, he identifies the single change that untangles dozens of hidden issues at once. His teammates swear he can see the application before it renders. They are not wrong.',
  },
  relatedJourneys: [],
  meritBadges: [],
};

/** @type {HeroProfile} */
export const nanoviolet = {
  slug: 'nanoviolet',
  crewSlug: 'jasmine-b',
  realName: 'Jasmine B.',
  realRole: 'DevOps Engineer and Platform Guardian',
  heroName: 'Nanoviolet',
  heroTitle: 'Guardian of the Invisible Stack',
  heroQuote: 'Deploy with confidence. Recover with certainty.',
  shortOrigin: 'Armed with billions of intelligent nanites and the ability to command ultraviolet energy, Nanoviolet protects the invisible systems behind every deployment, API call, and customer interaction.',
  primaryPower: 'Ultraviolet Observability',
  specialAbility: 'Nanite Auto-Heal',
  powerSource: 'Programmable Nanites and Ultraviolet Energy',
  headquarters: 'The Repo',
  status: 'Active',
  originHeading: 'Nanoviolet: Guardian of the Invisible Stack',
  originDeck: 'Some heroes charge into battle. Nanoviolet prevents the battle from ever happening.',
  powersHeading: 'Nanoviolet Super Powers',
  powersDeck: 'She does not just maintain systems. She makes them resilient.',
  closingQuote: 'Deploy with confidence. Recover with certainty.',
  realImage: jasmineImage,
  heroImage: jasmineHeroImage,
  placeholderImage: heroPlaceholderImage,
  artAlt: 'Nanoviolet character art',
  originChapters: [
    {
      id: 'guardian-before-the-surge',
      label: 'Chapter 01',
      title: 'Before the Surge',
      lead: "Before becoming Nanoviolet, she was Reckon's DevOps engineer, the person everyone relied on when production was on the line.",
      paragraphs: [
        'She believed the best engineering was not measured by flashy features, but by the reliability of the systems quietly powering them.',
        'While others built features, she protected the pipelines, containers, dependencies, and infrastructure humming beneath the surface.',
      ],
    },
    {
      id: 'ultraviolet-event',
      label: 'Chapter 02',
      title: 'The Ultraviolet Event',
      lead: 'Late one night, an automated release collided with an experimental swarm of programmable nanites.',
      paragraphs: [
        'She was testing a next-generation self-healing deployment platform deep inside The Repo when the collision released a surge of ultraviolet energy.',
        'Instead of destroying the nanites, the surge merged them with her nervous system. When the light faded, everything had changed.',
      ],
    },
    {
      id: 'invisible-stack',
      label: 'Chapter 03',
      title: 'The Invisible Stack',
      lead: 'She could now see every layer of the software stack simultaneously.',
      paragraphs: [
        'Build pipelines flowed through CI. Containers appeared across Kubernetes clusters. Infrastructure shifted in real time. Dependencies, certificates, logs, traces, and metrics glowed like violet constellations across the digital landscape.',
        'The nanites became extensions of her mind, allowing her to repair infrastructure, rebuild environments, and heal failing systems before users ever experienced an outage.',
      ],
    },
    {
      id: 'protector-of-production',
      label: 'Chapter 04',
      title: 'Protector of Production',
      lead: 'Today, Nanoviolet patrols The Repo, hunting invisible problems before they become production incidents.',
      paragraphs: [
        'While other heroes battle bugs on the front lines, she is already reinforcing the infrastructure beneath their feet.',
        'The strongest software is not the software that never fails. It is the software that knows how to recover.',
      ],
    },
    {
      id: 'journey-that-built-the-hero',
      label: 'The Reckon Way',
      title: 'The Journey That Built the Hero',
      lead: 'Her path was built one production release, one late-night incident, and one lesson in resilience at a time.',
      paragraphs: [
        'Every cloud certification earned, pipeline automated, Kubernetes cluster stabilized, and outage resolved strengthened her ability to protect the invisible systems behind modern software.',
        'Those real-world experiences became the foundation for a hero who proves that the best engineering is often the engineering no one notices, because everything simply works.',
      ],
    },
  ],
  powers: [
    {
      name: 'Ultraviolet Observability',
      icon: 'mdi-lightning-bolt-circle',
      artwork: powerArtwork('nanoviolet', 'ultraviolet-observability'),
      description: 'Sees every log, metric, trace, deployment, and dependency across The Repo, revealing problems long before alerts begin to sound.',
    },
    {
      name: 'Nanite Auto-Heal',
      icon: 'mdi-robot-outline',
      artwork: powerArtwork('nanoviolet', 'nanite-auto-heal'),
      description: 'Commands billions of intelligent nanites to repair infrastructure, restart unhealthy services, patch vulnerabilities, and recover failing environments automatically.',
    },
    {
      name: 'Pipeline Acceleration',
      icon: 'mdi-rocket-launch-outline',
      artwork: powerArtwork('nanoviolet', 'pipeline-acceleration'),
      description: 'Optimizes CI/CD pipelines in real time, transforming slow, fragile deployments into fast, reliable releases.',
    },
    {
      name: 'Infrastructure Forge',
      icon: 'mdi-cloud-cog-outline',
      artwork: powerArtwork('nanoviolet', 'infrastructure-forge'),
      description: 'Materializes cloud infrastructure from pure thought, provisioning containers, networking, storage, and compute wherever the team needs it.',
    },
    {
      name: 'Zero-Downtime Barrier',
      icon: 'mdi-shield-check-outline',
      artwork: powerArtwork('nanoviolet', 'zero-downtime-barrier'),
      description: 'Surrounds production environments with a protective energy field, allowing deployments, upgrades, and maintenance without interrupting users.',
    },
    {
      name: 'Dependency Vision',
      icon: 'mdi-eye-circle-outline',
      artwork: powerArtwork('nanoviolet', 'dependency-vision'),
      description: 'Instantly detects outdated libraries, configuration drift, expiring certificates, and hidden vulnerabilities before they impact production.',
    },
  ],
  archNemesis: {
    label: 'Favorite Enemy',
    name: 'The Configuration Drift',
    description: 'A shape-shifting villain that silently changes production environments one tiny setting at a time, leaving developers convinced, "But it worked on my machine." Nanoviolet spots Drift\'s fingerprints instantly and restores order before the next deployment spirals into chaos.',
  },
  relatedJourneys: [],
  meritBadges: [],
};

/** @type {HeroProfile} */
export const benThePlumber = {
  slug: 'ben-the-plumber',
  crewSlug: 'ben-b',
  realName: 'Ben B.',
  realRole: 'Backend Engineer',
  heroName: 'Ben The Plumber',
  heroTitle: 'Master of the Hidden Pipelines',
  heroQuote: 'The best systems are the ones nobody notices... until they stop flowing.',
  shortOrigin: "Guardian of software's hidden pipelines, Ben sees every request, bottleneck, and broken dependency before it reaches production.",
  primaryPower: 'Flow Vision',
  specialAbility: 'Pipeline Mastery',
  powerSource: 'The Source Pipeline',
  headquarters: 'The Repo',
  status: 'Active',
  originHeading: 'Ben the Plumber: Master of the Hidden Pipelines',
  originDeck: 'Every software application is like a city. Ben protects the invisible network that keeps everything moving.',
  powersHeading: 'Ben the Plumber Super Powers',
  powersDeck: 'He follows the flow, fixes the foundation, and keeps production healthy.',
  closingQuote: "Great software isn't built on the screens you see. It's built on the pipelines you never have to think about.",
  realImage: benImage,
  heroImage: benHeroImage,
  placeholderImage: heroPlaceholderImage,
  artAlt: 'Ben The Plumber character art',
  originChapters: [
    {
      id: 'beneath-the-city',
      label: 'Chapter 01',
      title: 'Beneath the City',
      lead: 'Users see the storefronts, streets, and skyline. Ben sees what keeps the city alive.',
      paragraphs: [
        "As one of Reckon's backend engineers, Ben built the APIs, databases, message queues, and services that everyone depended on but few ever saw.",
        'When deployments stalled, integrations failed, or production started acting strangely, he never chased the symptoms. He followed the flow.',
      ],
    },
    {
      id: 'source-pipeline',
      label: 'Chapter 02',
      title: 'The Source Pipeline',
      lead: 'Late one evening inside The Repo, Ben tracked a mysterious outage into a forgotten corner of legacy infrastructure.',
      paragraphs: [
        'Hidden beneath decades of abandoned services and deprecated APIs was something no engineer had ever documented: the legendary Source Pipeline, the first service from which every modern system had unknowingly evolved.',
        'The moment Ben touched it, a surge of living runtime energy raced through every endpoint, database connection, and event stream in the system.',
      ],
    },
    {
      id: 'guardian-of-the-flow',
      label: 'Chapter 03',
      title: 'Guardian of the Flow',
      lead: 'When the lights came back on, Ben had become something entirely new.',
      paragraphs: [
        "Whenever he enters The Repo, he transforms into Ben the Plumber, guardian of software's hidden pipelines.",
        'He sees every request before it is made, every bottleneck before it forms, and every broken dependency before it reaches production. Armed with the Flowbreaker Wrench, he repairs failing services and reconnects fractured integrations.',
        'While other heroes battle bugs on the surface, Ben protects the infrastructure beneath it all.',
      ],
    },
    {
      id: 'the-mission',
      label: 'Mission',
      title: 'Never Let the Pipes Burst',
      lead: 'Every application depends on something users never see.',
      paragraphs: [
        'APIs. Databases. Queues. Services. Infrastructure. Ben keeps those hidden systems running so the rest of the team can build with confidence.',
        'Keep the data flowing. Keep production healthy. Never let the pipes burst.',
      ],
    },
  ],
  powers: [
    {
      name: 'Flow Vision',
      icon: 'mdi-eye-circle-outline',
      artwork: powerArtwork('ben-the-plumber', 'flow-vision'),
      description: 'Sees every API call, database query, event stream, and service dependency as glowing pipelines, making hidden problems impossible to hide.',
    },
    {
      name: 'Pipeline Mastery',
      icon: 'mdi-pipe-wrench',
      artwork: powerArtwork('ben-the-plumber', 'pipeline-mastery'),
      description: 'Repairs broken integrations, CI/CD pipelines, and backend services with precision, restoring production before anyone notices.',
    },
    {
      name: 'Pressure Control',
      icon: 'mdi-gauge',
      artwork: powerArtwork('ben-the-plumber', 'pressure-control'),
      description: 'Balances traffic across distributed systems, preventing bottlenecks, outages, and cascading failures before they begin.',
    },
    {
      name: 'Leak Detector',
      icon: 'mdi-shield-search',
      artwork: powerArtwork('ben-the-plumber', 'leak-detector'),
      description: 'Tracks down memory leaks, resource exhaustion, and data corruption with unmatched accuracy.',
    },
    {
      name: 'Legacy Whisperer',
      icon: 'mdi-history',
      artwork: powerArtwork('ben-the-plumber', 'legacy-whisperer'),
      description: 'Navigates decades-old codebases, modernizing fragile systems without disrupting the applications that rely on them.',
    },
  ],
  signatureGear: {
    name: 'The Flowbreaker Wrench',
    description: "Forged from the energy of the Source Pipeline, Ben's wrench can tighten loose integrations, reconnect broken services, and repair even the most stubborn legacy code. In the wrong hands it is just a tool. In Ben's hands, it is the difference between uptime and chaos.",
  },
  relatedJourneys: [],
  meritBadges: [],
};

/** @type {HeroProfile} */
export const fireMarshalPhil = {
  slug: 'fire-marshal-phil',
  crewSlug: 'phillip-h',
  realName: 'Phillip H.',
  realRole: 'Software Engineer',
  heroName: 'Fire Marshall Phil',
  heroTitle: 'Master of Production Fires',
  heroQuote: 'Every system catches fire eventually. Heroes are the ones who keep it running.',
  shortOrigin: 'A production support specialist forged in the Eternal Build Forge, Phil controls software fires before they become disasters.',
  primaryPower: 'Production Fire Control',
  specialAbility: 'Hotfix Cannon',
  powerSource: 'The Eternal Build Forge',
  headquarters: 'The Repo',
  status: 'Active',
  originHeading: 'Fire Marshall Phil: Master of Production Fires',
  originDeck: 'Most developers run from production outages. Phil runs toward them.',
  powersHeading: 'Fire Marshall Phil Super Powers',
  powersDeck: 'Production Support. Incident Response. Reliability Engineering. Hotfixes.',
  closingQuote: 'Stay calm. Every production fire can be contained.',
  realImage: phillipImage,
  heroImage: phillipHeroImage,
  placeholderImage: heroPlaceholderImage,
  artAlt: 'Fire Marshal Phil character art',
  originChapters: [
    {
      id: 'engineer-who-walked-into-fire',
      label: 'Chapter 01',
      title: 'The Engineer Who Walked Into the Fire',
      lead: 'Long before becoming a hero, Phillip was the engineer who stayed calm when everything else was falling apart.',
      paragraphs: [
        'While dashboards flashed red, phones rang nonstop, and Slack erupted with alerts, Phil quietly worked the problem, finding the root cause while everyone else was still reacting.',
        'His teammates joked that he had a sixth sense for production fires. Then one deployment changed everything.',
      ],
    },
    {
      id: 'eternal-build-forge',
      label: 'Chapter 02',
      title: 'The Eternal Build Forge',
      lead: 'Deep inside The Repo, Phil discovered an abandoned deployment engine fueled by decades of failed releases.',
      paragraphs: [
        'Broken merges, emergency hotfixes, forgotten services, and technical debt had made the forge dangerously unstable.',
        'When it erupted, waves of corrupted code spread through The Repo, igniting production systems across the landscape. Everyone evacuated. Phil did not.',
      ],
    },
    {
      id: 'forged-in-the-inferno',
      label: 'Chapter 03',
      title: 'Forged in the Inferno',
      lead: 'Phil stepped into the heart of the inferno and manually stabilized the release.',
      paragraphs: [
        'The explosion did not destroy him. It transformed him. The Build Forge fused with his code, giving him complete control over software fires.',
        'He could see production incidents before alerts triggered, contain failures before they spread, and extinguish outages without bringing entire systems offline.',
        'Today, when production starts burning, Fire Marshall Phil is already on the scene. Every fire leaves clues, and Phil always knows where to look.',
      ],
    },
    {
      id: 'the-heros-mission',
      label: 'Mission',
      title: "The Hero's Mission",
      lead: 'Production will always throw surprises at you.',
      paragraphs: [
        'Deployments fail. Dependencies break. Infrastructure melts down. Fire Marshall Phil exists for those moments.',
        'He protects customers from outages, keeps teammates focused under pressure, and reminds everyone that the best engineers are not the ones who never see production fires. They are the ones who know exactly how to put them out.',
      ],
    },
    {
      id: 'journey-that-built-fire-marshal-phil',
      label: 'The Reckon Way',
      title: 'The Journey That Built the Hero',
      lead: 'Every great hero is shaped by experience.',
      paragraphs: [
        'Phillip spent years responding to outages, stabilizing critical systems, supporting high-impact government missions, and helping engineering teams navigate high-pressure incidents where reliability mattered most.',
        'Each deployment, emergency hotfix, and late-night production issue did not just build an engineer. They forged Fire Marshall Phil.',
      ],
    },
  ],
  powers: [
    {
      name: 'Production Fire Control',
      icon: 'mdi-fire-circle',
      artwork: powerArtwork('fire-marshal-phil', 'production-fire-control'),
      description: 'Detects outages before they spread, isolates failing services, and extinguishes production incidents before customers ever notice.',
    },
    {
      name: 'Hotfix Cannon',
      icon: 'mdi-fire-truck',
      artwork: powerArtwork('fire-marshal-phil', 'hotfix-cannon'),
      description: 'Deploys precision fixes that resolve critical bugs without introducing new ones.',
    },
    {
      name: 'Thermal Debug Vision',
      icon: 'mdi-thermometer-alert',
      artwork: powerArtwork('fire-marshal-phil', 'thermal-debug-vision'),
      description: 'Sees overloaded servers, memory leaks, failing APIs, and bottlenecks as glowing heat signatures throughout The Repo.',
    },
    {
      name: 'Incident Containment Field',
      icon: 'mdi-shield-alert-outline',
      artwork: powerArtwork('fire-marshal-phil', 'incident-containment-field'),
      description: 'Builds protective barriers around unstable deployments, preventing cascading failures across connected systems.',
    },
    {
      name: 'Root Cause Radar',
      icon: 'mdi-radar',
      artwork: powerArtwork('fire-marshal-phil', 'root-cause-radar'),
      description: 'Cuts through noise to identify the true source of an incident instead of chasing symptoms.',
    },
    {
      name: 'Build Forge Core',
      icon: 'mdi-cog-refresh-outline',
      artwork: powerArtwork('fire-marshal-phil', 'build-forge-core'),
      description: 'Absorbs the lessons from failed deployments and converts them into stronger, more resilient systems.',
    },
  ],
  relatedJourneys: [],
  meritBadges: [],
};

/** @type {HeroProfile[]} */
export const heroProfiles = [
  professorK,
  doctorStrongarm,
  helix,
  captainBrandoRando,
  mrIncredulous,
  gigabrain,
  nanoviolet,
  benThePlumber,
  fireMarshalPhil,
];

export const heroProfileBySlug = heroProfiles.reduce((profiles, profile) => ({
  ...profiles,
  [profile.slug]: profile,
}), {});

export const heroProfileByCrewSlug = heroProfiles.reduce((profiles, profile) => ({
  ...profiles,
  [profile.crewSlug]: profile,
}), {});
